import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CalendarClock, CheckCircle2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { StudentBalance, fetchStudentBalances } from '../lib/balance'

export function ZeroBalancePanel() {
  const [atZero, setAtZero] = useState<StudentBalance[] | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [tutorNames, setTutorNames] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const [balances, pending, tutors] = await Promise.all([
          fetchStudentBalances(),
          supabase
            .from('sessions')
            .select('id', { count: 'exact', head: true })
            .eq('approval_status', 'pending')
            .neq('lifecycle_status', 'cancelled'),
          supabase.from('tutors').select('id, name'),
        ])
        if (pending.error) throw pending.error
        setAtZero(balances.filter(b => b.balanceMinutes <= 0).sort((a, b) => a.balanceMinutes - b.balanceMinutes))
        setPendingCount(pending.count ?? 0)
        setTutorNames(Object.fromEntries((tutors.data ?? []).map((t: any) => [t.id, t.name])))
      } catch (e: any) {
        setError(
          e?.code === '42P01'
            ? 'Balance view missing. Run migrations/v6-zero-balance.sql on this database.'
            : e?.message || 'Could not load balances.'
        )
      }
    })()
  }, [])

  if (error) {
    return <div className="mb-6 p-3 rounded-lg bg-red-500/10 text-sm text-red-600 dark:text-red-300">{error}</div>
  }
  if (!atZero) return null

  const urgent = atZero.length > 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <Link
        to="/dashboard/sessions"
        className={`rounded-lg shadow p-6 border-2 transition-colors ${
          urgent
            ? 'bg-red-50 dark:bg-red-900/20 border-red-400 hover:border-red-500'
            : 'bg-white dark:bg-surface-100 border-transparent hover:border-brand-400/50'
        }`}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-3 rounded-lg ${urgent ? 'bg-red-500' : 'bg-brand-600'}`}>
            <CalendarClock className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300">Sessions awaiting approval</h3>
        </div>
        <p className={`text-3xl font-bold ${urgent ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
          {pendingCount}
        </p>
        {urgent && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">
            {atZero.length} student{atZero.length === 1 ? ' is' : 's are'} at zero or below. Check balances before approving.
          </p>
        )}
      </Link>

      <div className="lg:col-span-2 bg-white dark:bg-surface-100 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {urgent ? <AlertTriangle className="h-5 w-5 text-red-500" /> : <CheckCircle2 className="h-5 w-5 text-green-500" />}
            Students at zero or below
          </h2>
          <span className={`text-sm font-medium ${urgent ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>{atZero.length}</span>
        </div>
        {urgent ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-72 overflow-y-auto">
            {atZero.map(s => (
              <li key={s.id}>
                <Link
                  to={`/dashboard/students/${s.id}`}
                  className="flex items-center justify-between gap-3 px-6 py-3 hover:bg-gray-50 dark:hover:bg-surface-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {s.tutorId ? tutorNames[s.tutorId] ?? 'Unknown tutor' : 'No tutor'}
                      {s.pendingMinutes > 0 && ` · ${s.pendingMinutes} min waiting for approval`}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400 whitespace-nowrap">
                    {s.balanceMinutes} min
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">Every student has minutes left.</p>
        )}
      </div>
    </div>
  )
}
